import React, { useState } from 'react';
import { Box, Button, IconButton, Popover } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useListPageStyles } from './styles';

export type TableActionMenuItem = {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  color?: 'inherit' | 'primary' | 'error' | 'secondary' | 'success' | 'info' | 'warning';
  disabled?: boolean;
};

type TableActionMenuProps = {
  items: TableActionMenuItem[];
  /** Stop row click when opening the menu (for clickable table rows) */
  stopPropagation?: boolean;
  ariaLabel?: string;
};

export const TableActionMenu = ({
  items,
  stopPropagation = true,
  ariaLabel = 'Actions',
}: TableActionMenuProps) => {
  const { classes } = useListPageStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent | object) => {
    if (event && 'stopPropagation' in event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton size="small" aria-label={ariaLabel} onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { className: classes.actionMenu },
        }}>
        <Box display="flex" flexDirection="column" gap={0.25}>
          {items.map((item) => (
            <Button
              key={item.label}
              className={classes.actionMenuButton}
              color={item.color ?? 'inherit'}
              disabled={item.disabled}
              startIcon={item.icon}
              onClick={(e) => {
                e.stopPropagation();
                handleClose(e);
                item.onClick();
              }}>
              {item.label}
            </Button>
          ))}
        </Box>
      </Popover>
    </>
  );
};
